import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  BanIcon,
  CalendarIcon,
  ChevronRightIcon,
  DollarSignIcon,
  HourglassIcon,
  TrophyIcon,
  XIcon,
} from "lucide-react";
import {
  ResponseBattleType as Battle,
  BattlesType,
  Ticket,
} from "@dripit/types";
import isEmpty from "lodash.isempty";
import isNil from "lodash.isnil";

import {
  Button,
  SizeInput,
  VerticalScrollList,
  ConfirmSelectionDialog,
} from "@/components/main";
import { cn } from "@/helpers/tailwind";
import {
  delay,
  formatBalance,
  formatCurrency,
  getBattleArtists,
  getBattleStatus,
  getPotentialProfit,
  getUserBattleStatus,
  usdcToUsd,
  usdToUsdc,
} from "@/helpers";
import { Separator, toast } from "@/components/ui";
import { RootState } from "@/store";
import { Cta } from "./BattleCardUtils";
import {
  BattleStatus,
  Contract,
  ContractFunc,
  UserBattleStatus,
  EnhancedBattleArtist,
} from "@/types";
import { useWeb3Query } from "@/hooks";
import { useBreakpoints } from "@/hooks";
import { BuyDialog } from "./BuyDialog";
import {
  BuyContainer,
  POSITION_TYPES,
  TosQuickAgreement,
} from "./BettingPanel/BettingPanelComponents";
import { NewBattleLabel } from "./NewBattleLabel";

export interface BattleCardProps {
  battle: Partial<Battle>;
  refetchBattles?: () => void;
  className?: string;
}

export const BattleCard: React.FC<BattleCardProps> = ({
  battle,
  refetchBattles,
  className,
}) => {
  const navigate = useNavigate();
  const { isGreaterThan } = useBreakpoints();
  const { balance } = useSelector((state: RootState) => state.wallet);
  const { data: profileData } = useSelector(
    (state: RootState) => state.profile
  );
  const { type, total: volume } = battle;
  const status = React.useMemo(() => getBattleStatus(battle), [battle]);
  const artists = React.useMemo(
    () => getBattleArtists(battle?.battleSides, "odds"),
    [battle.battleSides]
  );
  const userStatus = React.useMemo(() => getUserBattleStatus(battle), [battle]);
  const [size, setSize] = React.useState<number | string>("");
  const [selectedArtist, setSelectedArtist] =
    React.useState<EnhancedBattleArtist | null>(null);
  const [showBuyStep, setShowBuyStep] = React.useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [buyDialogOpen, setBuyDialogOpen] = React.useState(false);
  const [selectedPositionType, setSelectedPositionType] =
    React.useState<string>(POSITION_TYPES[0].id);
  const [selectedTicketIds, setSelectedTicketIds] = React.useState<
    Ticket["tokenId"][]
  >([]);
  const selectedBattleSide = React.useMemo(
    () =>
      battle?.battleSides?.find(
        (side) => side?.artist_id === selectedArtist?.id
      ),
    [battle?.battleSides, selectedArtist?.id]
  );
  const potentialProfit = React.useMemo(
    () =>
      getPotentialProfit(battle ?? {}, selectedBattleSide, {
        amount: usdToUsdc(size).toString(),
      }),
    [battle, selectedBattleSide, size]
  );
  const ticketSize = React.useMemo(
    () =>
      selectedPositionType !== "tickets"
        ? 0
        : selectedTicketIds.reduce((total, ticketId) => {
            const ticket = profileData?.tickets?.find(
              ({ tokenId }) => tokenId === ticketId
            );
            return total + usdcToUsd(Number(ticket?.amount ?? 0));
          }, 0),
    [selectedTicketIds, profileData?.tickets]
  );
  const [makePrediction, { loading: makePredictionLoading }] = useWeb3Query(
    selectedPositionType === "wallet"
      ? ContractFunc.MAKE_PREDICTION
      : ContractFunc.MAKE_PREDICTION_WITH_TICKETS,
    {
      onSuccess: async () => {
        toast({
          title: "Side Selected Successfully",
          description: `You chose ${selectedArtist?.name} in this Battle.`,
        });
        setShowBuyStep(false);
        setBuyDialogOpen(false);
        setConfirmDialogOpen(false);
        await delay(3000);
        refetchBattles?.();
      },
      onError: () => {
        toast({
          title: "Error",
          description: "Something went wrong while placing a prediction",
          variant: "destructive",
        });
      },
    }
  );
  const [addSizeToPrediction, { loading: addSizeLoading }] = useWeb3Query(
    selectedPositionType === "wallet"
      ? ContractFunc.ADD_SIZE
      : ContractFunc.ADD_SIZE_TICKETS,
    {
      onSuccess: async () => {
        toast({
          title: "Added Size Successfully",
          description: `You added size to your position on ${selectedArtist?.name} in this Battle.`,
        });
        setShowBuyStep(false);
        setBuyDialogOpen(false);
        setConfirmDialogOpen(false);
        await delay(3000);
        refetchBattles?.();
      },
      onError: () => {
        toast({
          title: "Error",
          description: "Something went wrong while adding size",
          variant: "destructive",
        });
      },
    }
  );

  React.useEffect(() => {
    if (selectedPositionType === "tickets") {
      setSize(ticketSize);
    } else {
      setSize("");
    }
  }, [ticketSize, selectedPositionType, setSize]);

  React.useEffect(() => {
    // clear size and selected artist when buy step is closed
    let timer: NodeJS.Timeout;
    if (!showBuyStep) {
      timer = setTimeout(() => {
        setSize("");
        setSelectedArtist(null);
      }, 350);
    }
    return () => clearTimeout(timer);
  }, [showBuyStep]);

  const getArtistPosition = (sideId?: EnhancedBattleArtist["battleSideId"]) => {
    if (isNil(sideId) || isNil(profileData?.id)) return undefined;
    const battleSide = battle?.battleSides?.find((side) => side?.id === sideId);
    const position =
      battleSide?.positions?.find(({ user_id }) => user_id === profileData?.id)
        ?.amount ?? null;

    if (!isNil(position)) {
      return Number(position ?? 0);
    }

    return undefined;
  };

  const positionLabel = (position?: number | null) =>
    !isNil(position) &&
    position > 0 && (
      <div
        className={cn(
          "rounded-md px-1.5 py-1 text-xs font-semibold",
          "bg-indigo-400/15 text-indigo-400",
          {
            "bg-teal-400/15 text-teal-400": userStatus === UserBattleStatus.Won,
            "bg-red-400/15 text-red-400 opacity-50":
              userStatus === UserBattleStatus.Lost,
            "bg-orange-400/15 text-orange-400":
              userStatus === UserBattleStatus.Pending,
            "bg-slate-700 text-slate-400 opacity-50":
              userStatus === UserBattleStatus.Canceled,
          }
        )}>
        <p className="truncate">$ {formatCurrency(usdcToUsd(position))}</p>
      </div>
    );

  const handleArtistSelect = (artist: EnhancedBattleArtist) => {
    if (isGreaterThan("lg")) {
      setSelectedArtist(artist);
      setShowBuyStep(true);
    } else {
      setSelectedArtist(artist);
      setBuyDialogOpen(true);
    }
  };

  const handleBuy = async () => {
    const minAmount = 1;
    const amount = Number(size);
    const battleInactive = dayjs().isAfter(
      dayjs((battle?.end_date ?? 0) * 1000)
    );
    const contractAddress =
      battle?.type === BattlesType.head2head_battle
        ? Contract.HEAD2HEAD_BATTLES
        : Contract.HYPEBOARD_BATTLES;

    if (battleInactive) {
      toast({
        title: "Error",
        description: "Battle is not active anymore.",
        variant: "destructive",
      });
      setShowBuyStep(false);
      setBuyDialogOpen(false);
      return;
    }

    if (amount > Number(balance) || amount < minAmount) {
      toast({
        title: "Error",
        description:
          amount < minAmount
            ? "Amount is less than minimum amount."
            : "Amount is invalid.",
        variant: "destructive",
      });
      return;
    }
    const txData = {
      battleId: battle?.onchainId,
      option: selectedBattleSide?.side_id ?? 0,
      amount,
      permitSig: "0x",
      useRelayer: false,
    };
    const hasPositionsOnSelectedSide = selectedBattleSide?.positions?.some(
      (position) => position?.user_id === profileData?.id
    );

    if (hasPositionsOnSelectedSide) {
      await addSizeToPrediction(contractAddress, txData);
    } else {
      await makePrediction(contractAddress, txData);
    }
  };

  const getBattlePeriod = React.useMemo(() => {
    if (!battle?.start_date || !battle?.end_date) return "Weekly";
    const startDate = dayjs(battle.start_date * 1000);
    const endDate = dayjs(battle.end_date * 1000);
    const daysDiff = endDate.diff(startDate, "day");

    if (daysDiff <= 1) return "Daily";
    if (daysDiff > 1 && daysDiff <= 7) return "Weekly";
    if (daysDiff >= 29) return "Monthly";
    return "Weekly";
  }, [battle?.start_date, battle?.end_date]);

  const contentHype = (
    <VerticalScrollList
      showFades
      fadeClassName="from-slate-800"
      containerClassName={cn(
        "flex flex-col gap-2",
        "max-h-[160px] h-full xl:max-h-[160px] lg:max-h-[205px]"
      )}>
      {artists.map((artist) => (
        <div
          key={artist.id}
          className={cn(
            "flex w-full items-center justify-between gap-3 text-slate-400"
          )}>
          <img
            src={artist.image ?? ""}
            alt={artist.name}
            className="size-9 rounded-sm"
          />
          <p className="flex-grow truncate text-sm font-medium">
            {artist.name}
          </p>
          {positionLabel(getArtistPosition(artist.battleSideId))}
          <p className="text-lg font-medium">{artist.odds?.toFixed(0) ?? 0}%</p>
          <Cta
            battle={battle}
            minimal
            status={userStatus}
            position={getArtistPosition(artist.battleSideId)}
            artistId={artist.id}
            winnerId={battle.winner_id}
            onClick={() => handleArtistSelect(artist)}
            refetchBattles={refetchBattles}
          />
        </div>
      ))}
    </VerticalScrollList>
  );

  const content1v1 = (
    <div className="flex justify-between gap-3">
      {artists.map((artist) => (
        <div
          key={artist.id}
          className={cn(
            "h-40 bg-cover bg-center bg-no-repeat object-cover bg-blend-darken",
            "relative basis-1/2 overflow-hidden rounded-xl",
            "flex flex-col justify-end p-2"
          )}
          style={{ backgroundImage: `url(${artist.image})` }}>
          <div className="z-10 text-slate-400">
            <p className="truncate text-sm font-medium">{artist.name}</p>
            <div className="mb-1.5 flex items-center justify-between">
              <p className="text-lg font-medium">
                {artist.odds?.toFixed(0) ?? 0}%
              </p>
              {positionLabel(getArtistPosition(artist.battleSideId))}
            </div>
            <Cta
              battle={battle}
              status={userStatus}
              position={getArtistPosition(artist.battleSideId)}
              artistId={artist.id}
              winnerId={battle.winner_id}
              onClick={() => handleArtistSelect(artist)}
              refetchBattles={refetchBattles}
            />
          </div>
          <div
            className={cn(
              "absolute inset-x-0 bottom-0 z-0 h-full",
              "bg-gradient-to-t from-slate-900 from-[0%] via-slate-900 via-[20%] to-slate-900/10"
            )}
          />
        </div>
      ))}
    </div>
  );

  const contentBuy = (
    <div
      className={cn(
        "absolute left-0 top-0 z-20 h-full w-full overflow-auto bg-slate-800",
        "flex flex-col justify-between",
        "opacity-100 transition-all duration-300 ease-in-out",
        { "pointer-events-none opacity-0": !showBuyStep }
      )}>
      <div className="flex w-full items-center justify-between gap-3 text-slate-400">
        <img
          src={selectedArtist?.image ?? ""}
          alt={selectedArtist?.name ?? ""}
          className="size-9 rounded-sm"
        />
        <p className="flex-grow truncate">{selectedArtist?.name}</p>
        {positionLabel(getArtistPosition(selectedArtist?.id ?? 0))}
        <p className="text-xl">{selectedArtist?.odds?.toFixed(0) ?? 0}%</p>
      </div>
      <Separator className="my-2 h-0 border-t border-dashed border-slate-600" />
      <div className="flex-grow">
        {/* min amount */}
        <div className="mb-0.5 mr-[128px] flex items-center justify-between">
          <p className="text-sm">Amount</p>
          <p className="font-geist text-xs text-indigo-300">
            <span className="mr-2">Min:</span>
            <span>$ 1</span>
          </p>
        </div>
        {/* input */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex-grow">
            <SizeInput
              size={size}
              setSize={setSize}
              disabled={makePredictionLoading || addSizeLoading}
            />
          </div>
          <div className="w-[120px] rounded-md bg-green-400/15 p-1.5 text-center text-green-300">
            <span className="mb-1 block text-[10px] leading-[10px]">
              Potential profit
            </span>
            <div className="flex items-center justify-center gap-1 overflow-hidden text-[11px] font-semibold leading-[10px]">
              <span className="truncate">
                $ {(potentialProfit.amount ?? 0).toFixed(2)}
              </span>
              <span className="h-3 w-0 border border-r-green-300" />
              <span>{(potentialProfit.percentage ?? 0).toFixed(0)}%</span>
            </div>
          </div>
        </div>
        {/* balance */}
        <div className="mr-[128px] mt-0.5 flex items-center justify-between gap-2 text-xs text-slate-400">
          <span>Balance:</span>
          <span>$ {formatBalance(Number(balance ?? 0))}</span>
        </div>
      </div>
      <Button
        variant="tertiary"
        loading={makePredictionLoading || addSizeLoading}
        onClick={() => setConfirmDialogOpen(true)}>
        <DollarSignIcon />
        <span>Buy</span>
        <span className="h-4 w-0 border border-l-transparent border-r-indigo-300" />
        <span className="font-light text-indigo-300">
          {selectedArtist?.name ?? ""}
        </span>
      </Button>
    </div>
  );

  return (
    <>
      <div
        className={cn(
          "relative flex flex-col justify-between rounded-3xl bg-slate-800 p-6",
          "border border-transparent transition-colors duration-200 ease-in-out",
          {
            "border-indigo-400":
              showBuyStep &&
              [
                BattlesType.hypeboard_battle,
                BattlesType.head2head_battle,
              ].includes(type as BattlesType),
            "border-green-400":
              showBuyStep && type === BattlesType.artist_hype_change,
          },
          className
        )}>
        <div className="mb-4 flex items-center justify-between gap-4">
          <h3
            className="line-clamp-2 w-full cursor-pointer"
            onClick={() => navigate(`/battles/${battle?.id}`)}>
            {battle?.name}
          </h3>
          {status === BattleStatus.Open && (
            <NewBattleLabel
              className="md:!text-sm"
              createdAt={battle?.created_at}
            />
          )}
          <div className="flex items-start gap-1">
            {type === BattlesType.hypeboard_battle && status === "pending" && (
              <div
                className={cn(
                  "size-7 rounded-md bg-orange-400/15 px-1.5 py-1 text-orange-400",
                  "flex items-center justify-center"
                )}>
                <HourglassIcon className="size-4" />
              </div>
            )}
            {type === BattlesType.hypeboard_battle &&
              status === BattleStatus.Canceled && (
                <div
                  className={cn(
                    "size-7 rounded-md bg-slate-700 px-1.5 py-1 text-slate-400",
                    "flex items-center justify-center"
                  )}>
                  <BanIcon className="size-4" />
                </div>
              )}
            <div className="relative">
              <Button
                className={cn(
                  "bg-slate-900 text-white hover:bg-slate-900/70",
                  "size-7 flex-shrink-0 border border-slate-700 p-0",
                  "opacity-100 transition-all duration-200 ease-in-out",
                  { "pointer-events-none opacity-0": showBuyStep }
                )}
                onClick={() => navigate(`/battles/${battle?.id}`)}>
                <ChevronRightIcon />
              </Button>
              <Button
                className={cn(
                  "hover:text-initial bg-white text-slate-900 hover:bg-white/80",
                  "size-7 flex-shrink-0 border border-slate-700 p-0",
                  "absolute right-0 top-0",
                  "opacity-100 transition-all duration-200 ease-in-out",
                  { "pointer-events-none opacity-0": !showBuyStep }
                )}
                disabled={makePredictionLoading || addSizeLoading}
                onClick={() => setShowBuyStep(false)}>
                <XIcon />
              </Button>
            </div>
          </div>
        </div>
        <div className="relative flex flex-grow flex-col">
          {contentBuy}
          <div className="mb-4 flex-grow">
            {type === BattlesType.hypeboard_battle && contentHype}
            {type === BattlesType.head2head_battle && content1v1}
          </div>
          <div className="flex justify-between gap-3">
            <div className="flex items-center gap-1 text-xs text-slate-500">
              <TrophyIcon className="size-3.5" />
              <span>Volume:</span>
              <span className="text-white">
                $ {formatBalance(usdcToUsd(Number(volume ?? 0)))}
              </span>
            </div>
            <div className="flex items-center gap-1 text-xs text-slate-500">
              <CalendarIcon className="size-3.5" />
              <span>{getBattlePeriod}</span>
            </div>
          </div>
        </div>
      </div>
      <BuyDialog
        open={buyDialogOpen}
        setOpen={setBuyDialogOpen}
        artistInfo={
          isNil(selectedArtist)
            ? undefined
            : {
                image: selectedArtist.image,
                name: selectedArtist.name,
                percentage: selectedArtist?.odds ?? 0,
              }
        }
        content={
          <BuyContainer
            size={size}
            ticketsTotalAmount={0}
            makePredictionLoading={makePredictionLoading}
            addSizeLoading={addSizeLoading}
            potentialProfit={potentialProfit}
            selectedPositionType={selectedPositionType}
            setSelectedPositionType={setSelectedPositionType}
            setSelectedTicketIds={setSelectedTicketIds}
            setSize={setSize}
          />
        }
        buyButton={
          <Button
            variant="tertiary"
            loading={makePredictionLoading || addSizeLoading}
            disabled={
              selectedPositionType === "wallet"
                ? size === "" || Number(size) < 1
                : isEmpty(selectedTicketIds)
            }
            onClick={handleBuy}>
            <DollarSignIcon />
            <span>Buy</span>
          </Button>
        }
        tosQuickAgreement={<TosQuickAgreement />}
      />
      <ConfirmSelectionDialog
        open={confirmDialogOpen}
        setOpen={setConfirmDialogOpen}
        artist={selectedArtist ?? undefined}
        amount={Number(size)}
        potentialProfit={potentialProfit ?? { amount: 0, percentage: 0 }}
        loading={makePredictionLoading || addSizeLoading}
        onConfirm={handleBuy}
      />
    </>
  );
};
